import { Alert, Box, IconButton, Stack, Tooltip } from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import ReplayIcon from '@mui/icons-material/Replay';
import StopIcon from '@mui/icons-material/Stop';
import type { GridColDef } from '@mui/x-data-grid';
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { resources } from '../../core/services/resources';
import { extractErrorMessage } from '../../core/services/api';
import { PageHeader } from '../../shared/components/PageHeader';
import { DataTable } from '../../shared/components/DataTable/DataTable';
import { StatusChip } from '../../shared/components/StatusChip';
import { formatDate } from '../../shared/utils/format';
import type { ReportInstance, ReportInstanceStatus } from '../../core/types/data-models';

/**
 * Status transitions surfaced as a row-level icon button. The renderer
 * picks the entry by the row's current `status`; statuses that don't
 * appear here (e.g. DONE) render no action because there's nothing
 * meaningful to flip to from the list view.
 */
const STATUS_ACTIONS: Partial<
  Record<
    ReportInstanceStatus,
    { label: string; icon: typeof PlayArrowIcon; nextStatus: ReportInstanceStatus }
  >
> = {
  PENDING:  { label: 'Run',    icon: PlayArrowIcon, nextStatus: 'ACTIVE' },
  FAILED:   { label: 'Re-run', icon: ReplayIcon,    nextStatus: 'ACTIVE' },
  DISABLED: { label: 'Re-run', icon: ReplayIcon,    nextStatus: 'ACTIVE' },
  ACTIVE:   { label: 'Stop',   icon: StopIcon,      nextStatus: 'DISABLED' },
};

export function ReportInstancesPage() {
  const queryClient = useQueryClient();
  const [pagination, setPagination] = useState({ page: 0, pageSize: 25 });
  const [error, setError] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['report-instances', pagination],
    queryFn: () =>
      resources.reportInstances.list({
        page: pagination.page + 1,
        pageSize: pagination.pageSize,
        sort: '-id',
      }),
    placeholderData: (prev) => prev,
  });

  // Template catalog → name lookup. Shared cache key with anywhere else
  // that needs to resolve `reportTemplateId` to a display name, so it's
  // fetched at most once per session.
  const templates = useQuery({
    queryKey: ['report-templates', 'select'],
    queryFn: () => resources.reportTemplates.list({ pageSize: 500, sort: 'name' }),
  });
  const templateNameById = useMemo(() => {
    const map = new Map<number, string>();
    for (const t of templates.data?.data ?? []) map.set(t.id, t.name);
    return map;
  }, [templates.data]);

  // PUT just the `status` field — leaves the rest of the row alone. The
  // backend's update schema for ReportInstance accepts partial bodies.
  const updateStatus = useMutation({
    mutationFn: (args: { id: number; status: ReportInstanceStatus }) =>
      resources.reportInstances.update(args.id, { status: args.status } as Partial<ReportInstance>),
    onSuccess: () => {
      setError(null);
      queryClient.invalidateQueries({ queryKey: ['report-instances'] });
    },
    onError: (err) => setError(extractErrorMessage(err)),
  });

  const columns: GridColDef<ReportInstance>[] = useMemo(
    () => [
      { field: 'id', headerName: 'ID', width: 80 },
      {
        field: 'reportTemplateId',
        headerName: 'Template',
        width: 240,
        // Resolve numeric id → human-readable template name; fall back to
        // `#<id>` while the catalog is loading or for rows whose template
        // isn't in the current page.
        renderCell: (p) => {
          const id = p.value as number | null | undefined;
          if (id == null) return '';
          return templateNameById.get(id) ?? `#${id}`;
        },
      },
      {
        field: 'status',
        headerName: 'Status',
        width: 130,
        renderCell: (p) => <StatusChip value={p.value as string} />,
      },
      {
        field: 'priority',
        headerName: 'Priority',
        width: 130,
        renderCell: (p) => <StatusChip value={p.value as string} />,
      },
      {
        field: 'createdAt',
        headerName: 'Started',
        width: 200,
        valueFormatter: (v) => formatDate(v as string),
      },
      {
        field: 'generateDate',
        headerName: 'Generated',
        width: 200,
        valueFormatter: (v) => (v ? formatDate(v as string) : ''),
      },
      {
        field: 'updatedAt',
        headerName: 'Updated',
        width: 200,
        valueFormatter: (v) => formatDate(v as string),
      },
      {
        field: '__action',
        headerName: '',
        sortable: false,
        filterable: false,
        // Two IconButtons (open + status action) + DataGrid cell padding.
        width: 130,
        align: 'right',
        headerAlign: 'right',
        renderCell: (p) => {
          const row = p.row as ReportInstance;
          const action = STATUS_ACTIONS[row.status];
          const pending = updateStatus.isPending && updateStatus.variables?.id === row.id;
          return (
            <Stack
              direction="row"
              spacing={0.5}
              alignItems="center"
              justifyContent="flex-end"
              sx={{ height: '100%' }}
            >
              {/* Mirror of the "open" affordance from Report Templates.
                  The ReportInstance detail page isn't built yet, so this is
                  a no-op for now — keeping the icon visible advertises the
                  intended UX. Wire `onClick={() => navigate(...)}` when
                  the route exists. */}
              <Tooltip title="Open details — coming soon">
                <span>
                  <IconButton
                    size="small"
                    color="primary"
                    aria-label={`open report instance ${row.id} (coming soon)`}
                  >
                    <OpenInNewIcon fontSize="small" />
                  </IconButton>
                </span>
              </Tooltip>
              {/* Status command: Run / Re-run / Stop. Only rendered when a
                  transition is defined for the row's current status (DONE
                  is terminal — no icon). Disabled while this row's
                  mutation is in flight so double-clicks can't double-fire. */}
              {action && (
                <Tooltip title={action.label}>
                  <span>
                    <IconButton
                      size="small"
                      color={action.nextStatus === 'DISABLED' ? 'error' : 'primary'}
                      disabled={pending}
                      onClick={() => updateStatus.mutate({ id: row.id, status: action.nextStatus })}
                      aria-label={`${action.label.toLowerCase()} report instance ${row.id}`}
                    >
                      <action.icon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              )}
            </Stack>
          );
        },
      },
    ],
    [templateNameById, updateStatus],
  );

  return (
    // Flex column so the DataTable can claim the remaining viewport
    // height. Same pattern as `CrudPage` and the detail-page tabs —
    // without `flex: 1, minHeight: 0` the table falls back to its 540 px
    // default instead of filling the available area.
    <Box sx={{ display: 'flex', flexDirection: 'column', flex: 1, minHeight: 0 }}>
      <PageHeader title="Report instances" description="Each row is a single template run." />
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <DataTable<ReportInstance>
        rows={list.data?.data ?? []}
        columns={columns}
        loading={list.isFetching}
        rowCount={list.data?.page.total ?? 0}
        paginationMode="server"
        paginationModel={pagination}
        onPaginationModelChange={setPagination}
        errorMessage={list.isError ? extractErrorMessage(list.error) : undefined}
      />
    </Box>
  );
}
